import { Api } from '@deepcloud/api-base'
import { mergeConfig } from '@deepcloud/http-client'
import { withQueryParams } from '../../../shared'
import type {
  HttpClient,
  HttpClientRequestConfig,
} from '@deepcloud/http-client'
import type {
  EmployeeGroupRequest,
  NextEditorRequest,
  TicketDirectSaveRequest,
  TicketGetListParams,
  TicketListResponse,
  TicketMarkAsUnusedParams,
  TicketMarkAsUsedParams,
  TicketResponse,
  TicketSaveRequest,
} from '../../@types'

export class TicketAPI extends Api {
  constructor(httpClient: HttpClient) {
    super(httpClient)
  }

  /**
   * Get Projects
   * -------------------
   * Errors:
   * - none documented
   */
  async getProjects(config?: HttpClientRequestConfig) {
    const path = `/ticket/projects`

    return this.httpClient.get<string[]>(path, config)
  }

  /**
   * Get Ticket List
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getList(
    params?: TicketGetListParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/ticket/tickets`

    return this.httpClient.get<TicketListResponse>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Get Ticket By Nr
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getByNr(ticketNr: string, config?: HttpClientRequestConfig) {
    const path = `/ticket/${ticketNr}`

    return this.httpClient.get<TicketResponse>(path, config)
  }

  /**
   * Publish Ticket
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async publish(
    payload: TicketDirectSaveRequest,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/ticket/publish`

    return this.httpClient.post<Record<string, unknown>>(path, payload, config)
  }

  /**
   * Keep Original
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async keepOriginal(ticketNr: string, config?: HttpClientRequestConfig) {
    const path = `/ticket/${ticketNr}/keep_original`

    return this.httpClient.post<Record<string, unknown>>(
      path,
      undefined,
      config,
    )
  }

  /**
   * Save Ticket
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async save(payload: TicketSaveRequest, config?: HttpClientRequestConfig) {
    const path = `/ticket/save`

    return this.httpClient.post<Record<string, unknown>>(path, payload, config)
  }

  /**
   * Mark Ticket As Unused
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async markAsUnused(
    ticketNr: string,
    params?: TicketMarkAsUnusedParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/ticket/unused/${ticketNr}`, params)

    return this.httpClient.post<unknown>(path, undefined, config)
  }

  /**
   * Mark Ticket As Used
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async markAsUsed(
    ticketNr: string,
    params?: TicketMarkAsUsedParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/ticket/used/${ticketNr}`, params)

    return this.httpClient.post<unknown>(path, undefined, config)
  }

  /**
   * Add Next Editor
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async addNextEditor(
    payload: NextEditorRequest,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/ticket/next_editor`

    return this.httpClient.post<Record<string, string>>(path, payload, config)
  }

  /**
   * Add Employee Group
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async addEmployeeGroup(
    payload: EmployeeGroupRequest,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/ticket/employeegroup`

    return this.httpClient.post<Record<string, string>>(path, payload, config)
  }
}
